/**
 * Wire types mirroring ai.libx.js (OpenAI-style chat). We type the transport
 * structurally via `ChatLike`, so an ai.libx.js `AIClient` is a drop-in — and a
 * `FakeAIClient` works in tests — with no hard runtime dependency on ai.libx.js.
 */

export type Role = 'system' | 'user' | 'assistant' | 'tool';

export interface ToolCall {
  id: string;
  type: 'function';
  function: { name: string; arguments: string }; // arguments is a JSON string
}

/** One part of a multimodal message (mirrors ai.libx.js ContentPart) — text or an image URL/data-URI. */
export interface ContentPart {
  type: 'text' | 'image_url';
  text?: string;
  image_url?: { url: string };
}

/** A message's content is either plain text or an array of multimodal parts (images + text). */
export type MessageContent = string | ContentPart[];

export interface Message {
  role: Role;
  content: MessageContent;
  name?: string;
  tool_call_id?: string;
  tool_calls?: ToolCall[];
}

/** Flatten any message content to its text (string as-is; parts → concatenated text) — for length
 *  estimation, summaries, and display. Non-text parts (images) contribute a short placeholder. */
export function contentText(content: MessageContent | undefined): string {
  if (content == null) return '';
  if (typeof content === 'string') return content;
  return content.map((p) => (p.type === 'text' ? (p.text ?? '') : '[image]')).join(p_sep(content));
}
const p_sep = (parts: ContentPart[]): string => (parts.length > 1 ? '\n' : '');

/** Build an image content part from a data-URI or http(s) URL. */
export function imagePart(url: string): ContentPart {
  return { type: 'image_url', image_url: { url } };
}

export interface Tool {
  type: 'function';
  function: { name: string; description?: string; parameters: object };
}

export interface ChatResponse {
  content: string;
  finishReason?: string;
  toolCalls?: ToolCall[];
  model?: string;
  usage?: { promptTokens: number; completionTokens: number; totalTokens: number };
}

/**
 * One incremental event from a streamed `chat({stream:true})` call — mirrors
 * ai.libx.js's `StreamChunk` (OpenAI-style): each chunk carries a `content`
 * text delta; the terminal chunk carries `finishReason` and the accumulated
 * `toolCalls`. Consuming the stream and folding the deltas reconstructs the
 * same `ChatResponse` the non-stream path returns.
 */
export interface StreamChunk {
  content: string;
  finishReason?: string;
  index?: number;
  toolCalls?: ToolCall[]; // accumulated tool calls (typically on the final chunk)
  reasoningContent?: string;
  usage?: { promptTokens: number; completionTokens: number; totalTokens: number }; // on the terminal chunk, when the provider reports it
}

export interface ChatOptions {
  model: string;
  messages: Message[];
  tools?: Tool[];
  toolChoice?: unknown;
  stream?: boolean;
  /** Cancel the request/stream. Forwarded to providers that honor it; the Agent also stops consuming on abort. */
  signal?: AbortSignal;
  [k: string]: unknown;
}

/** Minimal shape of an ai.libx.js AIClient that the Agent drives. */
export interface ChatLike {
  chat(options: ChatOptions): Promise<ChatResponse | AsyncIterable<StreamChunk>>;
}
